import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { SchedulePreferences } from "./models/SchedulePreferences";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import {
  fetchSchedulePreferences,
  saveSchedulePreferences,
} from "./slice-extensions/instructor-preferences-slice-extension";

type InitializationStatus = "not-initialized" | "initializing" | "initialized";

interface InstructorPreferencesState {
  preferences?: SchedulePreferences;
  status: LoadingStatus;
  saveStatus: LoadingStatus;
  initializationStatus: InitializationStatus;
  lastError?: ApiErrorResponse;
}

const initialState: InstructorPreferencesState = {
  preferences: undefined,
  status: LoadingStatus.Idle,
  saveStatus: LoadingStatus.Idle,
  initializationStatus: "not-initialized",
  lastError: undefined,
};

const instructorPreferencesSlice = createSlice({
  name: "instructorPreferences",
  initialState,
  reducers: {
    clearPreferencesError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedulePreferences.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.initializationStatus = "initializing";
        state.lastError = undefined;
      })
      .addCase(
        fetchSchedulePreferences.fulfilled,
        (state, action: PayloadAction<SchedulePreferences>) => {
          state.status = LoadingStatus.Idle;
          state.initializationStatus = "initialized";
          state.preferences = action.payload;
        }
      )
      .addCase(fetchSchedulePreferences.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.initializationStatus = "initialized";
        state.lastError = action.payload;
      })
      .addCase(saveSchedulePreferences.pending, (state) => {
        state.saveStatus = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(
        saveSchedulePreferences.fulfilled,
        (state, action: PayloadAction<SchedulePreferences>) => {
          state.saveStatus = LoadingStatus.Idle;
          state.preferences = action.payload;
        }
      )
      .addCase(saveSchedulePreferences.rejected, (state, action) => {
        state.saveStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearPreferencesError } = instructorPreferencesSlice.actions;

export const selectInstructorPreferences = (state: RootState) =>
  state.instructorPreferences.preferences;
export const selectPreferencesInitializationStatus = (state: RootState) =>
  state.instructorPreferences.initializationStatus;
export const selectPreferencesSaveStatus = (state: RootState) =>
  state.instructorPreferences.saveStatus;
export const selectPreferencesError = (state: RootState) =>
  state.instructorPreferences.lastError;

export default instructorPreferencesSlice.reducer;
